import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiEndpoint } from '../consts';
import fetchWithAuth, { fetchCampaigns } from '../apis';
import { CampaignData } from '../types';

/**
 * Query hook to fetch all campaigns for admin users
 * Implementation that doesn't depend on googleAdsCustomerId
 * Never caches results to ensure fresh data
 */
export function useAdminCampaignsQuery(orgId?: number | null) {
  // Direct implementation that doesn't use the conditional enabled flag
  // Explicitly set cache to false to prevent caching
  return useQuery({
    queryKey: ['adminCampaigns', 'all'],
    queryFn: () => fetchCampaigns(orgId),
    enabled: !!orgId,
  });
}

/**
 * Query hook to fetch only launched campaigns (LIVE and PAUSED) for admin users
 * Reuses the existing campaigns query with a filter applied
 * Never caches results to ensure fresh data
 */
export function useAdminLaunchedCampaignsQuery(orgId?: number | null) {
  // Create a new query object instead of spreading campaignsQueryOptions
  return useQuery({
    queryKey: ['adminCampaigns', 'launched'] as const,
    queryFn: () => fetchCampaigns(orgId),
    enabled: !!orgId,
    select: (data: CampaignData[]) => {
      // Filter campaigns that are either live or paused, handling string case-sensitivity
      return data.filter((campaign) => {
        const state = campaign.state?.toLowerCase?.() || '';
        return state === 'live' || state === 'paused';
      });
    },
  });
}

/**
 * Mutation hook to create a recommendation
 */
export function useCreateRecommendationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      campaignId,
      recommendationType,
      options,
      processImmediately = false,
    }: {
      campaignId: string;
      recommendationType: string;
      options: Record<string, unknown>;
      processImmediately?: boolean;
    }) => {
      // Make sure we're using the consistent URL format for both endpoints
      const endpoint = processImmediately
        ? `${apiEndpoint}/admin/campaigns/${campaignId}/recommendations/process`
        : `${apiEndpoint}/admin/campaigns/${campaignId}/recommendations`;

      const response = await fetchWithAuth(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          recommendation_type: recommendationType,
          options,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create recommendation');
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries when a recommendation is created
      queryClient.invalidateQueries({
        queryKey: ['actionLogs', variables.campaignId],
      });
    },
  });
}

/**
 * Query hook to fetch recommendations for a campaign.
 *
 * This uses the existing /campaigns/{campaignId}/recommendations endpoint
 * which returns CampaignRecommendations with this shape:
 * {
 *   recommendations: [
 *     {
 *       id: number,
 *       campaign_id: number,
 *       title: string,
 *       severity: string,
 *       description: string,
 *       state: string,
 *       recommendation_type: string,
 *       created_at: string,
 *       ad_recommendations: Array
 *     }
 *   ]
 * }
 */
export function useRecommendationsQuery(campaignId: string) {
  return useQuery({
    queryKey: ['recommendations', campaignId],
    queryFn: async () => {
      try {
        // Use the existing campaign recommendations endpoint
        const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/recommendations`);

        if (!response.ok) {
          throw new Error('Failed to fetch recommendations');
        }

        const data = await response.json();

        // The response format is { recommendations: [...] }
        if (data && Array.isArray(data.recommendations)) {
          return data.recommendations;
        }

        // Fallback to empty array if data format is unexpected
        console.warn('Unexpected recommendation data format:', data);
        return [];
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        throw error;
      }
    },
    enabled: !!campaignId,
  });
}

/**
 * Query hook to fetch all recommendations across campaigns
 */
export function useAllRecommendationsQuery(
  filters: {
    recommendationType?: string;
    state?: string;
  } = {},
  pagination: {
    skip: number;
    limit: number;
  } = { skip: 0, limit: 100 },
) {
  const queryParams = new URLSearchParams();

  // Add filters
  if (filters.recommendationType) {
    queryParams.append('recommendation_type', filters.recommendationType);
  }

  if (filters.state) {
    queryParams.append('state', filters.state);
  }

  // Add pagination
  queryParams.append('skip', pagination.skip.toString());
  queryParams.append('limit', pagination.limit.toString());

  return useQuery({
    queryKey: ['allRecommendations', filters, pagination],
    queryFn: async () => {
      try {
        const response = await fetchWithAuth(`${apiEndpoint}/recommendations?${queryParams.toString()}`);

        if (!response.ok) {
          throw new Error('Failed to fetch recommendations');
        }

        return response.json();
      } catch (error) {
        console.error('Error fetching all recommendations:', error);
        throw error;
      }
    },
  });
}

/**
 * Variables for the process recommendation mutation
 */
export interface ProcessRecommendationVariables {
  recommendation_id?: number;
  recommendation_type?: string;
  options?: Record<string, unknown>;
}

/**
 * Mutation hook to process an existing recommendation
 */
export function useProcessRecommendation(campaignId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: ProcessRecommendationVariables) => {
      const endpoint = `${apiEndpoint}/admin/campaigns/${campaignId}/recommendations/process`;

      const response = await fetchWithAuth(endpoint, {
        method: 'POST',
        body: JSON.stringify(variables),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to process recommendation');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate relevant queries when a recommendation is processed
      queryClient.invalidateQueries({
        queryKey: ['actionLogs', campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ['recommendations', campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ['allRecommendations'],
      });
      // Invalidate the campaign tasks query
      queryClient.invalidateQueries({
        queryKey: ['campaignTasks', Number(campaignId)],
      });
      // Invalidate all action logs queries to refresh the main tasks list
      queryClient.invalidateQueries({
        queryKey: ['actionLogs'],
      });
      // Invalidate task tracking
      queryClient.invalidateQueries({
        queryKey: ['campaignTaskTracking'],
      });
    },
  });
}

/**
 * Query hook to fetch campaigns with insufficient keywords
 */
export const useInsufficientKeywordsCampaigns = (threshold: number = 10) => {
  return useQuery({
    queryKey: ['admin', 'insufficient-keywords-campaigns', threshold],
    queryFn: async () => {
      const response = await fetchWithAuth(
        `${apiEndpoint}/admin/campaigns/insufficient-keywords?threshold=${threshold}`,
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch campaigns with insufficient keywords');
      }

      return response.json();
    },
  });
};

/**
 * Query hook to fetch detailed information about a campaign's keywords
 */
export const useCampaignKeywordDetails = (campaignId: number) => {
  return useQuery({
    queryKey: ['admin', 'campaign-keyword-details', campaignId],
    queryFn: async () => {
      const response = await fetchWithAuth(`${apiEndpoint}/admin/campaigns/${campaignId}/keyword-details`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch campaign keyword details');
      }

      return response.json();
    },
    enabled: !!campaignId,
  });
};

/**
 * Mutation hook for generating keyword recommendations
 */
export const useGenerateKeywordRecommendations = () => {
  return useMutation({
    mutationFn: async ({ campaignId, count = 20 }: { campaignId: number; count?: number }) => {
      const response = await fetchWithAuth(
        `${apiEndpoint}/admin/campaigns/${campaignId}/keyword-recommendations?count=${count}`,
        {
          method: 'POST',
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate keyword recommendations');
      }

      return response.json();
    },
  });
};

/**
 * Mutation hook for applying keyword recommendations
 */
export const useApplyKeywordRecommendation = () => {
  return useMutation({
    mutationFn: async ({
      recommendationId,
      keywords,
      matchType = 'BROAD',
    }: {
      recommendationId: number;
      keywords: Array<{ text: string; matchType?: string }>;
      matchType?: string;
    }) => {
      const response = await fetchWithAuth(`${apiEndpoint}/admin/recommendations/${recommendationId}/apply`, {
        method: 'POST',
        body: JSON.stringify({
          keywords,
          match_type: matchType,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to apply keyword recommendation');
      }

      return response.json();
    },
  });
};

/**
 * Campaign summary type
 */
export interface CampaignSummary {
  campaign_id: number;
  campaign_name: string;
  account_name: string;
  user_name: string;
  action_count: number;
}

/**
 * Daily summary notification response type
 */
export interface DailySummaryResponse {
  success: boolean;
  message: string;
  notifications_created: number;
  users_processed: number;
  users_with_actions: number;
  total_actions_processed: number;
  errors: number;
  campaign_summaries: CampaignSummary[];
}

/**
 * Mutation hook for generating daily summary notifications
 */
export const useGenerateDailySummaryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ targetDate, userId, organizationId }: { targetDate?: Date; userId?: number; organizationId?: number | null}) => {
      // Choose endpoint based on whether organizationId is provided
      const endpoint = organizationId 
        ? `${apiEndpoint}/admin/organizations/${organizationId}/daily-summary`
        : `${apiEndpoint}/admin/daily-summary`;

      const response = await fetchWithAuth(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          target_date: targetDate ? targetDate.toLocaleDateString('sv-SE') : undefined,
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate daily summary notifications');
      }

      return response.json() as Promise<DailySummaryResponse>;
    },
    onSuccess: () => {
      // Invalidate notifications queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      });
    },
  });
};


/**
 * Manual notification creation request type
 */
export interface CreateNotificationRequest {
  title: string;
  subtitle?: string;
  content: string;
  source?: string;
  warning_level?: 'INFO' | 'WARNING' | 'CRITICAL' | 'SUCCESS';
  organization_id?: number | null;
}

/**
 * Notification creation response type
 */
export interface CreateNotificationResponse {
  id: string;
  title: string;
  content: string;
  warning_level: string;
  created_at: string;
}

/**
 * Mutation hook for creating manual notifications
 */
export const useCreateNotificationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateNotificationRequest): Promise<CreateNotificationResponse> => {
      const response = await fetchWithAuth(`${apiEndpoint}/notifications`, {
        method: 'POST',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create notification');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate notifications queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      });
    },
  });
};

/**
 * Weekly report response type
 */
export interface WeeklyReportResponse {
  success: boolean;
  message: string;
  organizations_processed: number;
  reports_created: number;
  campaigns_processed: number;
  errors: number;
}

/**
 * Strategic report response type
 */
export interface StrategicReportResponse {
  message: string;
}

/**
 * Intelligence report response type
 */
export interface IntelligenceReportResponse {
  task_id: number;
  status: string;
  message: string;
}

/**
 * Business option type for business selection
 */
export interface BusinessOption {
  id: number;
  brand_name: string;
  business_url?: string | null;
  vertical?: string | null;
  organization_id: number;
  organization_name: string;
}

/**
 * Mutation hook for generating weekly reports
 */
export const useGenerateWeeklyReportMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      reportWeekStart,
      reportWeekEnd,
      userId,
      organizationId,
      includeExternal = true,
    }: {
      reportWeekStart?: Date;
      reportWeekEnd?: Date;
      userId?: number;
      organizationId?: number;
      includeExternal?: boolean;
    }) => {
      const params = new URLSearchParams();

      if (userId) {
        params.append('user_id', userId.toString());
      }

      if (organizationId) {
        params.append('organization_id', organizationId.toString());
      }

      if (reportWeekStart) {
        params.append('report_week_start', reportWeekStart.toLocaleDateString('sv-SE'));
      }

      if (reportWeekEnd) {
        params.append('report_week_end', reportWeekEnd.toLocaleDateString('sv-SE'));
      }

      params.append('include_external', includeExternal.toString());
      
      const queryString = params.toString();
      const url = `${apiEndpoint}/organizations/${organizationId}/weekly-reports/generate${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetchWithAuth(url, {
        method: 'POST',
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate weekly reports');
      }
      
      return response.json() as Promise<WeeklyReportResponse>;
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['weeklyReports'],
      });
    },
  });
};

/**
 * Mutation hook for generating strategic reports
 */
export const useGenerateStrategicReportMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      organizationId,
      googleadsaccountId,
      businessId
    }: { 
      organizationId: number;
      googleadsaccountId: number;
      businessId?: number | null;
    }) => {
      const params = new URLSearchParams();
      params.append('organization_id', organizationId.toString());
      params.append('googleadsaccount_id', googleadsaccountId.toString());
      
      if (businessId) {
        params.append('business_id', businessId.toString());
      }
      
      const url = `${apiEndpoint}/strategic-reports/generate?${params.toString()}`;
      
      const response = await fetchWithAuth(url, {
        method: 'POST',
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate strategic report');
      }
      
      return response.json() as Promise<StrategicReportResponse>;
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['strategicReports'],
      });
      queryClient.invalidateQueries({
        queryKey: ['reports'],
      });
      queryClient.invalidateQueries({
        queryKey: ['task-tracking'],
      });
    },
  });
};

/**
 * Mutation hook for generating intelligence reports
 */
export const useGenerateIntelligenceReportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      googleadsaccountId,
      businessId
    }: {
      organizationId: number;
      googleadsaccountId?: number | null;
      businessId?: number | null;
    }) => {
      const params = new URLSearchParams();
      params.append('organization_id', organizationId.toString());
      if (googleadsaccountId) {
        params.append('googleadsaccount_id', googleadsaccountId.toString());
      }

      if (businessId) {
        params.append('business_id', businessId.toString());
      }
      
      const url = `${apiEndpoint}/intelligence-reports/generate?${params.toString()}`;
      
      const response = await fetchWithAuth(url, {
        method: 'POST',
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate intelligence report');
      }
      
      return response.json() as Promise<IntelligenceReportResponse>;
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['intelligenceReports'],
      });
      queryClient.invalidateQueries({
        queryKey: ['reports'],
      });
      queryClient.invalidateQueries({
        queryKey: ['task-tracking'],
      });
    },
  });
};

/**
 * Query hook to fetch available businesses for intelligence report generation
 */
export const useAvailableBusinesses = () => {
  return useQuery({
    queryKey: ['availableBusinesses'],
    queryFn: async () => {
      const response = await fetchWithAuth(`${apiEndpoint}/intelligence-reports/available-businesses`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch available businesses');
      }

      return response.json() as Promise<BusinessOption[]>;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Report generation task type
 */
export interface ReportGenerationTask {
  id: number;
  task_type: 'WEEKLY_REPORT_GENERATION' | 'STRATEGIC_REPORT_GENERATION';
  state: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  created_at: string;
  updated_at: string | null;
  organization_id: number | null;
  organization_name: string | null;
  error_message: string | null;
}

/**
 * Report generation tasks list response type
 */
export interface ReportGenerationTasksResponse {
  tasks: ReportGenerationTask[];
  total_count: number;
  has_active_tasks: boolean;
}

/**
 * Query hook to fetch report generation tasks
 */
export const useReportGenerationTasksQuery = (
  statusFilter?: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED',
  reportTypeFilter?: 'WEEKLY_REPORT_GENERATION' | 'STRATEGIC_REPORT_GENERATION',
) => {
  return useQuery({
    queryKey: ['reportGenerationTasks', statusFilter, reportTypeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (statusFilter) {
        params.append('status_filter', statusFilter);
      }

      if (reportTypeFilter) {
        params.append('report_type_filter', reportTypeFilter);
      }

      const queryString = params.toString();
      const url = `${apiEndpoint}/admin/report-generation-tasks${queryString ? `?${queryString}` : ''}`;

      const response = await fetchWithAuth(url);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch report generation tasks');
      }

      return response.json() as Promise<ReportGenerationTasksResponse>;
    },
    refetchInterval: (query) => {
      // Auto-refresh every 10 seconds if there are active tasks, otherwise don't auto-refresh
      const data = query.state.data;
      return data?.has_active_tasks ? 10000 : false;
    },
  });
};

